import { useState, useEffect } from 'react';
import { Expense } from '../lib/supabase';
import { getExpenseTypeNameFromData, normalizeExpenseTypeKey } from '../lib/expenseTypeHelpers';
import {
  Calendar,
  ChevronDown,
} from 'lucide-react';
import { usePreferences } from '../contexts/PreferencesContext';
import { formatCurrency } from '../lib/conversions';
import { theme } from '../lib/theme';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslations } from '../i18n';
import { estimateMileageAtDate } from '../lib/mileageUtils';
import defaultExpenseTypesData from '../data/defaultExpenseTypes.json';

type StatisticsProps = {
  logEntries: any[]; // Using any[] to bypass strict Expense type that no longer aligns with logEntries structure
};

type TypeStats = {
  type: string;
  total: number;
  count: number;
  percentage: number;
  topExpenses: Expense[];
  totalLiters?: number;
  totalElectricityKwh?: number;
  minAmount: number;
  maxAmount: number;
  minUnitPrice?: number;
  maxUnitPrice?: number;
};

type MonthlyStats = {
  month: string;
  total: number;
  year: number;
};

export function Statistics({ logEntries }: StatisticsProps) {
  const [selectedYear, setSelectedYear] = useState<number | 'all'>('all');
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [expandedYears, setExpandedYears] = useState<Set<number>>(new Set());
  const { preferences } = usePreferences();
  const { t, language } = useTranslations();
  const { ownerPreferences, selectedCar } = useOutletContext<any>() || {};

  const effectivePreferences = (ownerPreferences || preferences) ? {
    ...(ownerPreferences || preferences),
    distance_unit: selectedCar?.distance_unit || (ownerPreferences || preferences).distance_unit,
    volume_unit: selectedCar?.volume_unit || (ownerPreferences || preferences).volume_unit,
    currency: selectedCar?.currency || (ownerPreferences || preferences).currency,
    currency_symbol: selectedCar?.currency_symbol || (ownerPreferences || preferences).currency_symbol
  } : null;

  const getTypeName = (type: string) => {
    const key = normalizeExpenseTypeKey(type);
    const info = (defaultExpenseTypesData as any)[key];
    if (info) return getExpenseTypeNameFromData(info, language as any);
    return (t.expenses.typeLabels as any)[type] || type;
  };

  useEffect(() => {
    const years = [
      ...new Set(
        logEntries.map((e) => new Date(e.date).getFullYear())
      ),
    ].sort((a, b) => b - a);
    setAvailableYears(years.length > 0 ? years : [new Date().getFullYear()]);
  }, [logEntries]);

  const flatExpenses = useMemo(() => {
    return logEntries.flatMap(entry => (entry.expenses || []).map((exp: any) => ({
      ...exp,
      entry,
      date: entry.date
    })));
  }, [logEntries]);

  const filteredExpenses = selectedYear === 'all'
    ? flatExpenses
    : flatExpenses.filter((e) => new Date(e.date).getFullYear() === selectedYear);

  const totalExpenses = filteredExpenses
    .filter(e => e.type !== 'info')
    .reduce(
      (sum, e) => {
        if (!e.amount) return sum;
        return sum + parseFloat(e.amount.toString());
      },
      0
    );

  const typeStatsMap = filteredExpenses
    .filter((e: any) => e.type !== 'info')
    .reduce(
      (acc: Record<string, TypeStats>, expense: any) => {
        const typeKey = normalizeExpenseTypeKey(expense.type);
        if (!acc[typeKey]) {
          acc[typeKey] = {
            type: typeKey, // Store the KEY here, we'll translate for display
            total: 0,
            count: 0,
            percentage: 0,
            topExpenses: [],
            totalLiters: 0,
            totalElectricityKwh: 0,
            minAmount: Infinity,
            maxAmount: -Infinity,
          };
        }
        if (expense.amount) {
          const amt = parseFloat(expense.amount.toString());
          acc[typeKey].total += amt;
          acc[typeKey].minAmount = Math.min(acc[typeKey].minAmount, amt);
          acc[typeKey].maxAmount = Math.max(acc[typeKey].maxAmount, amt);
        }
        acc[typeKey].count += 1;
        acc[typeKey].topExpenses.push(expense);

        if (expense.type === 'fuel' && expense.fuel_liters) {
          const liters = parseFloat(expense.fuel_liters.toString());
          acc[typeKey].totalLiters = (acc[typeKey].totalLiters || 0) + liters;
          if (expense.amount && liters > 0) {
            const uPrice = parseFloat(expense.amount.toString()) / liters;
            acc[typeKey].minUnitPrice = acc[typeKey].minUnitPrice === undefined ? uPrice : Math.min(acc[typeKey].minUnitPrice!, uPrice);
            acc[typeKey].maxUnitPrice = acc[typeKey].maxUnitPrice === undefined ? uPrice : Math.max(acc[typeKey].maxUnitPrice!, uPrice);
          }
        }

        if (expense.type === 'electricity' && expense.electricity_kwh) {
          const kwh = parseFloat(expense.electricity_kwh.toString());
          acc[typeKey].totalElectricityKwh = (acc[typeKey].totalElectricityKwh || 0) + kwh;
          if (expense.amount && kwh > 0) {
            const uPrice = parseFloat(expense.amount.toString()) / kwh;
            acc[typeKey].minUnitPrice = acc[typeKey].minUnitPrice === undefined ? uPrice : Math.min(acc[typeKey].minUnitPrice!, uPrice);
            acc[typeKey].maxUnitPrice = acc[typeKey].maxUnitPrice === undefined ? uPrice : Math.max(acc[typeKey].maxUnitPrice!, uPrice);
          }
        }

        return acc;
      },
      {} as Record<string, TypeStats>
    );

  const typeStats: TypeStats[] = (Object.values(typeStatsMap) as TypeStats[])
    .map((stat: TypeStats) => ({
      ...stat,
      percentage: totalExpenses > 0 ? (stat.total / totalExpenses) * 100 : 0,
      topExpenses: stat.topExpenses
        .sort((a: any, b: any) => {
          const aAmount = a.amount ? parseFloat(a.amount.toString()) : 0;
          const bAmount = b.amount ? parseFloat(b.amount.toString()) : 0;
          return bAmount - aAmount;
        })
        .slice(0, 3),
    }))
    .sort((a: TypeStats, b: TypeStats) => b.total - a.total);

  const monthlyStats: MonthlyStats[] = (Object.entries(
    filteredExpenses
      .filter((e: any) => e.type !== 'info')
      .reduce(
        (acc: Record<string, MonthlyStats>, expense: any) => {
          const date = new Date(expense.date);
          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          if (!acc[monthKey]) {
            acc[monthKey] = {
              month: date.toLocaleDateString(language, {
                month: 'long',
                year: 'numeric',
              }),
              total: 0,
              year: date.getFullYear(),
            };
          }
          if (expense.amount) {
            acc[monthKey].total += parseFloat(expense.amount.toString());
          }
          return acc;
        },
        {} as Record<string, MonthlyStats>
      )
  ).map(([, value]) => value as MonthlyStats)
  ).sort((a: MonthlyStats, b: MonthlyStats) => {
    const aDate = new Date(a.month);
    const bDate = new Date(b.month);
    return bDate.getTime() - aDate.getTime();
  });

  const avgMonthly =
    monthlyStats.length > 0
      ? totalExpenses / (selectedYear === 'all' ? monthlyStats.length : 12)
      : totalExpenses;

  // Smart Distance Calculation for the selected period
  const getEstimationPoints = () => {
    if (selectedYear === 'all') {
      const dates = logEntries.map(e => new Date(e.date).getTime());
      if (dates.length === 0) return { start: new Date(), end: new Date() };
      return { 
        start: new Date(Math.min(...dates)), 
        end: new Date() // For 'all', end is basically today
      };
    } else {
      return {
        start: new Date(selectedYear, 0, 1), 
        end: new Date(selectedYear, 11, 31, 23, 59, 59)
      };
    }
  };

  const periodDates = getEstimationPoints();
  const estStart = selectedCar ? estimateMileageAtDate(selectedCar.purchase_mileage, selectedCar.bought_at, logEntries, selectedCar.estimated_yearly_km, periodDates.start) : 0;
  const estEnd = selectedCar ? estimateMileageAtDate(selectedCar.purchase_mileage, selectedCar.bought_at, logEntries, selectedCar.estimated_yearly_km, periodDates.end) : 0;
  
  const distance = Math.max(0, estEnd - estStart);
  const cpk = distance > 0 ? totalExpenses / distance : 0;

  const yearlyStats: MonthlyStats[] = (Object.entries(
    filteredExpenses
      .filter((e: any) => e.type !== 'info')
      .reduce(
        (acc: Record<string, MonthlyStats>, expense: any) => {
          const date = new Date(expense.date);
          const yearKey = date.getFullYear().toString();
          if (!acc[yearKey]) {
            acc[yearKey] = {
              month: yearKey,
              total: 0,
              year: date.getFullYear(),
            };
          }
          if (expense.amount) {
            acc[yearKey].total += parseFloat(expense.amount.toString());
          }
          return acc;
        },
        {} as Record<string, MonthlyStats>
      )
  ).map(([, value]) => value as MonthlyStats)
  ).sort((a: MonthlyStats, b: MonthlyStats) => b.year - a.year);

  const toggleYear = (year: number) => {
    const newExpanded = new Set(expandedYears);
    if (newExpanded.has(year)) {
      newExpanded.delete(year);
    } else {
      newExpanded.add(year);
    }
    setExpandedYears(newExpanded);
  };

  const getMonthsForYear = (year: number): MonthlyStats[] => {
    return (Object.entries(
      filteredExpenses
        .filter((expense: any) => new Date(expense.date).getFullYear() === year && expense.type !== 'info')
        .reduce(
          (acc: Record<string, MonthlyStats>, expense: any) => {
            const date = new Date(expense.date);
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            if (!acc[monthKey]) {
              acc[monthKey] = {
                month: date.toLocaleDateString(undefined, {
                  month: 'long',
                  year: 'numeric',
                }),
                total: 0,
                year: date.getFullYear(),
              };
            }
            if (expense.amount) {
              acc[monthKey].total += parseFloat(expense.amount.toString());
            }
            return acc;
          },
          {} as Record<string, MonthlyStats>
        )
    ).map(([, value]) => value as MonthlyStats)
    ).sort((a: MonthlyStats, b: MonthlyStats) => {
        const aDate = new Date(a.month);
        const bDate = new Date(b.month);
        return bDate.getTime() - aDate.getTime();
      });
  };

  const colors = [
    'bg-blue-500',
    'bg-green-500',
    'bg-yellow-500',
    'bg-red-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-orange-500',
    'bg-teal-500',
    'bg-cyan-500',
    'bg-gray-500',
  ];

  // Logic for Value/Cost Chart
  const chartData = useMemo(() => {
    if (filteredExpenses.length === 0) return [];

    // 1. Find all available value points from TOTAL history to have global context
    const allSorted = [...flatExpenses].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    const valPoints = allSorted
      .filter((e: any) => e.entry?.car_value && Number(e.entry.car_value) > 0)
      .map((e: any) => ({
        t: new Date(e.date).getTime(),
        v: Number(e.entry.car_value)
      }));

    // 2. Sort filtered expenses for the chart display - EXCLUDING empty info entries but INCLUDING value updates/purchase prices
    const displaySorted = filteredExpenses
      .filter((e: any) => e.type !== 'info' || (e.entry?.car_value && Number(e.entry.car_value) > 0) || (e.amount && Number(e.amount) > 0))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    let runningCosts = 0;
    let baselineValue: number | null = null;

    const data = displaySorted.map((e, idx) => {
      const amount = e.type !== 'info' && e.amount ? parseFloat(e.amount.toString()) : 0;
      runningCosts += amount;

      const currentTime = new Date(e.date).getTime();
      let estValue: number | null = null;

      // Interpolate from global value points
      if (valPoints.length >= 2) {
        if (currentTime <= valPoints[0].t) {
          estValue = valPoints[0].v;
        } else if (currentTime >= valPoints[valPoints.length - 1].t) {
          estValue = valPoints[valPoints.length - 1].v;
        } else {
          for (let i = 0; i < valPoints.length - 1; i++) {
            const p1 = valPoints[i];
            const p2 = valPoints[i + 1];
            if (currentTime >= p1.t && currentTime <= p2.t) {
              const ratio = (currentTime - p1.t) / (p2.t - p1.t);
              estValue = p1.v + (p2.v - p1.v) * ratio;
              break;
            }
          }
        }
      } else if (valPoints.length === 1) {
        estValue = valPoints[0].v;
      }

      // First point in the chart establishes the baseline for depreciation
      if (idx === 0) {
        baselineValue = estValue;
      }

      const depr = (baselineValue !== null && estValue !== null) ? (baselineValue - estValue) : 0;
      const burn = runningCosts + depr;

      return {
        date: e.date,
        shortDate: new Date(e.date).toLocaleDateString(language, { day: '2-digit', month: 'short' }),
        fullDate: new Date(e.date).toLocaleDateString(language, { day: '2-digit', month: 'short', year: 'numeric' }),
        [t.statistics.carMarketValue]: estValue,
        [t.statistics.cumulativeCosts]: runningCosts,
        [t.statistics.totalBurn]: burn
      };
    });

    console.log('--- Value Chart Debug ---');
    console.log('Value points found:', valPoints.length);
    if (valPoints.length > 0) {
      console.log('Oldest value point:', valPoints[0].v, 'at', new Date(valPoints[0].t).toLocaleDateString());
      console.log('Newest value point:', valPoints[valPoints.length - 1].v, 'at', new Date(valPoints[valPoints.length - 1].t).toLocaleDateString());
    }

    return data;
  }, [filteredExpenses, logEntries]);

  const fuelChartData = useMemo(() => {
    return filteredExpenses
      .filter(e => e.type === 'fuel' && e.amount && e.fuel_liters && parseFloat(e.fuel_liters.toString()) > 0)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .map(e => ({
        date: e.date,
        fullDate: new Date(e.date).toLocaleDateString(language, { day: '2-digit', month: 'short', year: 'numeric' }),
        [(t.statistics as any)?.fuelPriceTrend || 'Fuel Price']: parseFloat(e.amount!.toString()) / parseFloat(e.fuel_liters!.toString())
      }));
  }, [filteredExpenses, language, t.statistics]);

  const CustomChartTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-4 border border-gray-100 shadow-xl rounded-2xl min-w-[200px]">
          <p className="text-sm font-black text-gray-500 mb-2 uppercase tracking-widest">{data.fullDate}</p>
          <div className="space-y-1.5">
            {payload.map((entry: any) => (
              <div key={entry.name} className="flex items-center justify-between gap-4">
                <span className="text-xs font-bold" style={{ color: entry.color }}>{entry.name}:</span>
                <span className="text-sm font-black text-gray-900">
                  {effectivePreferences ? formatCurrency(entry.value, effectivePreferences.currency, effectivePreferences.currency_symbol) : `€${entry.value.toFixed(2)}`}
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  const serviceTrendData = useMemo(() => {
    const serviceKeys = ['service_repair', 'service_maintenance', 'service_tuning', 'repair'];
    
    // Group all expenses by year
    const byYear: Record<number, { year: number, repair: number, maintenance: number, upgrade: number }> = {};
    
    flatExpenses.forEach(e => {
        const type = normalizeExpenseTypeKey(e.type);
        if (serviceKeys.includes(type)) {
            const year = new Date(e.date).getFullYear();
            if (!byYear[year]) {
                byYear[year] = { year, repair: 0, maintenance: 0, upgrade: 0 };
            }
            const amount = e.amount ? parseFloat(e.amount.toString()) : 0;
            if (type === 'service_repair' || type === 'repair') {
                byYear[year].repair += amount;
            } else if (type === 'service_maintenance') {
                byYear[year].maintenance += amount;
            } else if (type === 'service_tuning') {
                byYear[year].upgrade += amount;
            }
        }
    });

    const sortedData = Object.values(byYear).sort((a, b) => a.year - b.year);
    
    // Add cumulative and trend info
    return sortedData.map((curr, idx) => {
        const prev = idx > 0 ? sortedData[idx - 1] : null;
        
        const getTrend = (currentVal: number, prevVal: number) => {
            if (!prevVal || prevVal === 0) return null;
            return ((currentVal - prevVal) / prevVal) * 100;
        };

        return {
            ...curr,
            fullYear: curr.year.toString(),
            repairTrend: prev ? getTrend(curr.repair, prev.repair) : null,
            maintenanceTrend: prev ? getTrend(curr.maintenance, prev.maintenance) : null,
            upgradeTrend: prev ? getTrend(curr.upgrade, prev.upgrade) : null
        };
    });
  }, [flatExpenses]);

  const ServiceTrendTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 border border-gray-100 shadow-xl rounded-2xl min-w-[220px]">
          <p className="text-sm font-black text-gray-500 mb-2 uppercase tracking-widest">{label}</p>
          <div className="space-y-2">
            {payload.map((entry: any) => {
              const trendKey = `${entry.dataKey}Trend`;
              const trend = entry.payload[trendKey];
              
              // Only repair/upgrade trend is "bad" if up. Maintenance is "good/neutral"? 
              // User just asked for +/-.
              const trendColor = trend > 0 ? 'text-red-500' : 'text-green-500';
              const trendSign = trend > 0 ? '+' : '';

              return (
                <div key={entry.name} className="flex flex-col border-b border-gray-50 last:border-0 pb-1.5 last:pb-0">
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-xs font-bold" style={{ color: entry.color }}>{entry.name}:</span>
                    <span className="text-sm font-black text-gray-900">
                      {effectivePreferences ? formatCurrency(entry.value, effectivePreferences.currency, effectivePreferences.currency_symbol) : `€${entry.value.toFixed(2)}`}
                    </span>
                  </div>
                  {trend !== null && (
                    <div className="flex justify-end">
                      <span className={`text-[10px] font-black ${trendColor} uppercase tracking-tighter`}>
                        {trendSign}{trend.toFixed(1)}%
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className={`${theme.spacing.pagePadding} pb-32 pt-4 relative`}>
      <div className="flex items-center justify-between px-1">
        <h3 className={theme.typography.sectionTitle}>
          {t.statistics.title}
        </h3>
        <div className="relative">
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
            className="appearance-none px-6 py-3 pr-12 bg-white dark:bg-slate-900/40 border-2 border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all font-semibold text-gray-800 dark:text-gray-100 cursor-pointer shadow-lg hover:shadow-xl"
          >
            <option value="all">{t.statistics.allYears}</option>
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
          <ChevronDown
            size={20}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-300 pointer-events-none"
          />
        </div>
      </div>

      <div className={`${theme.colors.cardBg} rounded-[2rem] shadow-xl dark:shadow-none p-6 border ${theme.colors.border} transition-all`}>
        <div className="grid grid-cols-3 gap-2">
          <div className="text-center">
            <p className="text-[11px] font-bold text-gray-400 uppercase tracking-tight mb-1">
              {selectedYear === 'all' ? t.statistics.totalSpent : t.statistics.spentIn.replace('{year}', selectedYear.toString())}
            </p>
            <p className="text-xl font-black text-gray-900 dark:text-white leading-tight">
              {effectivePreferences ? formatCurrency(totalExpenses, effectivePreferences.currency, effectivePreferences.currency_symbol) : `€${totalExpenses.toFixed(2)}`}
              <span className="block text-[13px] text-gray-400 dark:text-gray-300 font-medium mt-0.5">({filteredExpenses.length} {t.statistics.entries})</span>
            </p>
          </div>
          <div className="text-center border-x border-gray-100 dark:border-white/10 px-2">
            <p className="text-[11px] font-bold text-gray-400 uppercase tracking-tight mb-1">{t.expenses.stats.avgMonth}</p>
            <p className="text-xl font-black text-gray-900 dark:text-white leading-tight">
              {effectivePreferences ? formatCurrency(avgMonthly, effectivePreferences.currency, effectivePreferences.currency_symbol) : `€${avgMonthly.toFixed(2)}`}
              <span className="block text-[13px] text-gray-400 dark:text-gray-300 font-medium mt-0.5">{t.statistics.perMonth}</span>
            </p>
          </div>
          <div className="text-center">
            <p className="text-[11px] font-bold text-gray-400 uppercase tracking-tight mb-1">{t.expenses.stats.costPer}{effectivePreferences?.distance_unit === 'miles' ? t.settings.miles : t.settings.kilometers}</p>
            <p className="text-xl font-black text-gray-900 dark:text-white leading-tight">
              {effectivePreferences ? formatCurrency(cpk, effectivePreferences.currency, effectivePreferences.currency_symbol) : `€${cpk.toFixed(2)}`}
              <span className="block text-[13px] text-gray-400 dark:text-gray-300 font-medium mt-0.5">{t.statistics.overall}</span>
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between px-1 py-1">
            <h3 className={theme.typography.sectionTitle}>
              {t.statistics.byType}
            </h3>
          </div>
          <div className={`${theme.colors.cardBg} p-6 border ${theme.colors.border} rounded-[2rem]`}>
            {typeStats.length === 0 ? (
              <p className="text-gray-400 text-center py-8">
                {t.statistics.noData}
              </p>
            ) : (
              <div className="space-y-6">
                {typeStats.map((stat, index) => (
                  <div key={stat.type} className="pb-4 border-b border-gray-100 last:border-b-0">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-gray-800 dark:text-gray-200">
                        {getTypeName(stat.type)}
                      </span>
                      <span className="text-lg font-bold text-gray-900 dark:text-white">
                        {effectivePreferences ? formatCurrency(stat.total, effectivePreferences.currency, effectivePreferences.currency_symbol) : `€${stat.total.toFixed(2)}`}
                        <span className="text-sm font-medium text-gray-400 dark:text-gray-300 ml-1">({stat.count})</span>
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${colors[index % colors.length]} transition-all duration-500`}
                          style={{ width: `${stat.percentage}%` }}
                        />
                      </div>
                      <span className="text-sm text-gray-600 font-semibold w-12 text-right">
                        {stat.percentage.toFixed(0)}%
                      </span>
                    </div>

                    {stat.type === 'fuel' && stat.totalLiters && stat.totalLiters > 0 ? (
                      <div className="mt-2 text-xs text-gray-500 space-y-1">
                        <div className="flex gap-4">
                          <span>{effectivePreferences?.volume_unit === 'gallons' ? t.settings.gallons : t.settings.liters}: {stat.totalLiters.toFixed(2)} {effectivePreferences?.volume_unit === 'gallons' ? 'gal' : 'L'}</span>
                          {stat.count > 0 && (
                            <span>Avg: {effectivePreferences ? formatCurrency(stat.total / stat.count, effectivePreferences.currency, effectivePreferences.currency_symbol) : `€${(stat.total / stat.count).toFixed(2)}`}</span>
                          )}
                        </div>
                        {stat.minUnitPrice !== undefined && stat.maxUnitPrice !== undefined && (
                          <div className="flex gap-4">
                            <span>{(t.statistics as any)?.price || 'Price'}: {effectivePreferences?.currency_symbol || '€'}{stat.minUnitPrice.toFixed(3)} - {effectivePreferences?.currency_symbol || '€'}{stat.maxUnitPrice.toFixed(3)} / {effectivePreferences?.volume_unit === 'gallons' ? 'gal' : 'L'}</span>
                            <span>Avg: {effectivePreferences?.currency_symbol || '€'}{(stat.total / stat.totalLiters!).toFixed(3)}</span>
                          </div>
                        )}
                        {stat.topExpenses.length > 0 && (
                          <div>
                            <span className="font-semibold">Top: </span>
                            {stat.topExpenses.map((exp, i) => (
                              <span key={exp.id}>
                                {exp.title || 'Untitled'} ({exp.amount ? (effectivePreferences ? formatCurrency(parseFloat(exp.amount.toString()), effectivePreferences.currency, effectivePreferences.currency_symbol) : `€${parseFloat(exp.amount.toString()).toFixed(2)}`) : '-'} )
                                {i < stat.topExpenses.length - 1 ? ', ' : ''}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : stat.type === 'electricity' && stat.totalElectricityKwh && stat.totalElectricityKwh > 0 ? (
                      <div className="mt-2 text-xs text-gray-500 space-y-1">
                        <div className="flex gap-4">
                          <span>kWh: {stat.totalElectricityKwh.toFixed(2)} kWh</span>
                          {stat.count > 0 && (
                            <span>Avg: {effectivePreferences ? formatCurrency(stat.total / stat.count, effectivePreferences.currency, effectivePreferences.currency_symbol) : `€${(stat.total / stat.count).toFixed(2)}`}</span>
                          )}
                        </div>
                        {stat.minUnitPrice !== undefined && stat.maxUnitPrice !== undefined && (
                          <div className="flex gap-4">
                            <span>{(t.statistics as any)?.price || 'Price'}: {effectivePreferences?.currency_symbol || '€'}{stat.minUnitPrice.toFixed(3)} - {effectivePreferences?.currency_symbol || '€'}{stat.maxUnitPrice.toFixed(3)} / kWh</span>
                            <span>Avg: {effectivePreferences?.currency_symbol || '€'}{(stat.total / stat.totalElectricityKwh!).toFixed(3)}</span>
                          </div>
                        )}
                        {stat.topExpenses.length > 0 && (
                          <div>
                            <span className="font-semibold">Top: </span>
                            {stat.topExpenses.map((exp, i) => (
                              <span key={exp.id}>
                                {exp.title || 'Untitled'} ({exp.amount ? (effectivePreferences ? formatCurrency(parseFloat(exp.amount.toString()), effectivePreferences.currency, effectivePreferences.currency_symbol) : `€${parseFloat(exp.amount.toString()).toFixed(2)}`) : '-'} )
                                {i < stat.topExpenses.length - 1 ? ', ' : ''}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="mt-2 text-xs text-gray-500 space-y-1">
                        {stat.count > 0 && stat.minAmount !== Infinity && stat.maxAmount !== -Infinity && (
                          <div className="flex gap-4">
                            <span>{(t.statistics as any)?.price || 'Price'}: {effectivePreferences?.currency_symbol || '€'}{stat.minAmount.toFixed(2)} - {effectivePreferences?.currency_symbol || '€'}{stat.maxAmount.toFixed(2)}</span>
                            <span>Avg: {effectivePreferences?.currency_symbol || '€'}{(stat.total / stat.count).toFixed(2)}</span>
                          </div>
                        )}
                        {stat.topExpenses.length > 0 && (
                          <div>
                            <span className="font-semibold">{t.statistics.mostExpensive}: </span>
                            {stat.topExpenses.map((exp, i) => (
                              <span key={exp.id}>
                                {exp.title || t.statistics.untitled} ({exp.amount ? (effectivePreferences ? formatCurrency(parseFloat(exp.amount.toString()), effectivePreferences.currency, effectivePreferences.currency_symbol) : `€${parseFloat(exp.amount.toString()).toFixed(2)}`) : '-'} )
                                {i < stat.topExpenses.length - 1 ? ', ' : ''}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {selectedYear !== 'all' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between px-1 py-1">
              <h3 className={theme.typography.sectionTitle}>
                {t.statistics.byMonth}
              </h3>
            </div>
            <div className={`${theme.colors.cardBg} p-6 border ${theme.colors.border} rounded-[2rem]`}>
              {monthlyStats.length === 0 ? (
                <p className="text-gray-400 text-center py-8">
                  {t.statistics.noData}
                </p>
              ) : (
                <div className="space-y-3">
                  {monthlyStats.map((stat) => (
                    <div
                      key={stat.month}
                      className="flex items-center justify-between p-4 bg-gray-50 dark:bg-white/5 rounded-xl hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <Calendar size={20} className="text-blue-600" />
                        </div>
                        <span className="font-semibold text-gray-800 dark:text-gray-300 capitalize">
                          {stat.month}
                        </span>
                      </div>
                      <span className="text-xl font-bold text-gray-900 dark:text-white">
                        {effectivePreferences ? formatCurrency(stat.total, effectivePreferences.currency, effectivePreferences.currency_symbol) : `€${stat.total.toFixed(2)}`}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {selectedYear === 'all' && yearlyStats.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between px-1 py-1">
              <h3 className={theme.typography.sectionTitle}>
                {t.statistics.byYear}
              </h3>
            </div>
            <div className={`${theme.colors.cardBg} p-6 border ${theme.colors.border} rounded-[2rem]`}>
              <div className="space-y-3">
                {yearlyStats.map((stat) => {
                  const isExpanded = expandedYears.has(stat.year);
                  const monthsForYear = getMonthsForYear(stat.year);

                  return (
                    <div key={stat.year} className="bg-gray-50 dark:bg-white/5 rounded-xl overflow-hidden">
                      <div
                        onClick={() => toggleYear(stat.year)}
                        className="flex items-center justify-between p-4 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors cursor-pointer"
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-green-100 rounded-full">
                            <Calendar size={20} className="text-green-600" />
                          </div>
                          <span className="font-semibold text-gray-800 dark:text-gray-300">
                            {stat.year}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xl font-bold text-gray-900 dark:text-white">
                            {effectivePreferences ? formatCurrency(stat.total, effectivePreferences.currency, effectivePreferences.currency_symbol) : `€${stat.total.toFixed(2)}`}
                          </span>
                          <ChevronDown
                            size={20}
                            className={`text-gray-500 dark:text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                          />
                        </div>
                      </div>

                      {isExpanded && monthsForYear.length > 0 && (
                        <div className="px-4 pb-4 space-y-2">
                          {monthsForYear.map((month) => (
                            <div
                              key={month.month}
                              className="flex items-center justify-between p-3 bg-white dark:bg-white/5 rounded-lg"
                            >
                              <span className="text-sm text-gray-700 dark:text-gray-400 capitalize">
                                {month.month}
                              </span>
                              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                {effectivePreferences ? formatCurrency(month.total, effectivePreferences.currency, effectivePreferences.currency_symbol) : `€${month.total.toFixed(2)}`}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Value & Cost Analysis Chart */}
      {chartData.length >= 2 && (
        <div className="space-y-4 pt-8">
          <div className="flex items-center justify-between px-1 py-1">
            <h3 className={theme.typography.sectionTitle}>
              {t.statistics.assetAnalysis}
            </h3>
          </div>
          <div className={`${theme.colors.cardBg} rounded-[2rem] shadow-xl dark:shadow-none p-6 border ${theme.colors.border}`}>
            <div className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 20, right: 30, left: 10, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis
                    dataKey="date"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fontWeight: 600, fill: '#94a3b8' }}
                    tickFormatter={(val) => new Date(val).toLocaleDateString(language, { day: '2-digit', month: 'short', ...(selectedYear === 'all' ? { year: '2-digit' } : {}) })}
                    dy={10}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fontWeight: 600, fill: '#94a3b8' }}
                    tickFormatter={(val) => {
                      const symbol = effectivePreferences?.currency_symbol || '€';
                      const formattedVal = `${(val / 1000).toFixed(0)}k`;
                      const isPre = effectivePreferences?.currency === 'USD' || effectivePreferences?.currency === 'CAD' || effectivePreferences?.currency === 'AUD' || effectivePreferences?.currency === 'JPY';
                      return isPre ? `${symbol}${formattedVal}` : `${formattedVal}${symbol}`;
                    }}
                  />
                  <Tooltip
                    content={<CustomChartTooltip />}
                  />
                  <Legend
                    verticalAlign="top"
                    align="center"
                    wrapperStyle={{ paddingTop: 0, paddingBottom: 20, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}
                  />
                  <Line
                    type="monotone"
                    dataKey={t.statistics.carMarketValue}
                    stroke="#3b82f6"
                    strokeWidth={4}
                    dot={false}
                    activeDot={{ r: 6, strokeWidth: 0 }}
                  />
                  <Line
                    type="monotone"
                    dataKey={t.statistics.cumulativeCosts}
                    stroke="#10b981"
                    strokeWidth={3}
                    strokeDasharray="5 5"
                    dot={false}
                    activeDot={{ r: 6, strokeWidth: 0 }}
                  />
                  <Line
                    type="monotone"
                    dataKey={t.statistics.totalBurn}
                    stroke="#ef4444"
                    strokeWidth={4}
                    dot={false}
                    activeDot={{ r: 6, strokeWidth: 0 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <p className="mt-6 text-[11px] text-gray-400 font-medium italic text-center">
              {t.statistics.burnNote}
            </p>
          </div>
        </div>
      )}

      {/* Fuel Price Trend Chart */}
      {fuelChartData.length >= 2 && (
        <div className="space-y-4 pt-8">
          <div className="flex items-center justify-between px-1 py-1">
            <h3 className={theme.typography.sectionTitle}>
              {(t.statistics as any)?.fuelPriceTrend || 'Fuel Price Trend'}
            </h3>
          </div>
          <div className={`${theme.colors.cardBg} rounded-[2rem] shadow-xl dark:shadow-none p-6 border ${theme.colors.border}`}>
            <div className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={fuelChartData} margin={{ top: 20, right: 30, left: 10, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis
                    dataKey="date"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fontWeight: 600, fill: '#94a3b8' }}
                    tickFormatter={(val) => new Date(val).toLocaleDateString(language, { day: '2-digit', month: 'short', ...(selectedYear === 'all' ? { year: '2-digit' } : {}) })}
                    dy={10}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    domain={['auto', 'auto']}
                    tick={{ fontSize: 10, fontWeight: 600, fill: '#94a3b8' }}
                    tickFormatter={(val) => {
                      const symbol = effectivePreferences?.currency_symbol || '€';
                      const isPre = effectivePreferences?.currency === 'USD' || effectivePreferences?.currency === 'CAD' || effectivePreferences?.currency === 'AUD' || effectivePreferences?.currency === 'JPY';
                      return isPre ? `${symbol}${val.toFixed(2)}` : `${val.toFixed(2)}${symbol}`;
                    }}
                  />
                  <Tooltip
                    content={<CustomChartTooltip />}
                  />
                  <Legend
                    verticalAlign="top"
                    align="center"
                    wrapperStyle={{ paddingTop: 0, paddingBottom: 20, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}
                  />
                  <Line
                    type="monotone"
                    dataKey={(t.statistics as any)?.fuelPriceTrend || 'Fuel Price'}
                    stroke="#eab308"
                    strokeWidth={4}
                    dot={{ r: 3, fill: '#eab308', strokeWidth: 0 }}
                    activeDot={{ r: 6, strokeWidth: 0 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Service Trends Chart */}
      {serviceTrendData.length >= 2 && (
        <div className="space-y-4 pt-8">
          <div className="flex items-center justify-between px-1 py-1">
            <h3 className={theme.typography.sectionTitle}>
              {(t.statistics as any)?.serviceTrendsTitle || 'Service Trends'}
            </h3>
          </div>
          <div className={`${theme.colors.cardBg} rounded-[2rem] shadow-xl dark:shadow-none p-6 border ${theme.colors.border}`}>
            <div className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={serviceTrendData} margin={{ top: 20, right: 30, left: 10, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis
                    dataKey="fullYear"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fontWeight: 600, fill: '#94a3b8' }}
                    dy={10}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fontWeight: 600, fill: '#94a3b8' }}
                    tickFormatter={(val) => {
                      const symbol = effectivePreferences?.currency_symbol || '€';
                      const formattedVal = val >= 1000 ? `${(val / 1000).toFixed(0)}k` : val.toString();
                      const isPre = effectivePreferences?.currency === 'USD' || effectivePreferences?.currency === 'CAD' || effectivePreferences?.currency === 'AUD' || effectivePreferences?.currency === 'JPY';
                      return isPre ? `${symbol}${formattedVal}` : `${formattedVal}${symbol}`;
                    }}
                  />
                  <Tooltip
                    content={<ServiceTrendTooltip />}
                  />
                  <Legend
                    verticalAlign="top"
                    align="center"
                    wrapperStyle={{ paddingTop: 0, paddingBottom: 20, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}
                  />
                  <Line
                    type="monotone"
                    name={getTypeName('service_repair')}
                    dataKey="repair"
                    stroke="#EF4444"
                    strokeWidth={4}
                    dot={{ r: 4, fill: '#EF4444', strokeWidth: 0 }}
                    activeDot={{ r: 6, strokeWidth: 0 }}
                  />
                  <Line
                    type="monotone"
                    name={getTypeName('service_maintenance')}
                    dataKey="maintenance"
                    stroke="#34D399"
                    strokeWidth={4}
                    dot={{ r: 4, fill: '#34D399', strokeWidth: 0 }}
                    activeDot={{ r: 6, strokeWidth: 0 }}
                  />
                  <Line
                    type="monotone"
                    name={getTypeName('service_upgrade')}
                    dataKey="upgrade"
                    stroke="#8B5CF6"
                    strokeWidth={4}
                    dot={{ r: 4, fill: '#8B5CF6', strokeWidth: 0 }}
                    activeDot={{ r: 6, strokeWidth: 0 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
